import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { Column } from 'primereact/column'
import { InputSwitch } from 'primereact/inputswitch'
import Badge from '../../../components/ui/Badge'
import Button from '../../../components/ui/Button'
import type { GroupRef, RoleRef, UserWithRoles } from '../types'
import { rowActionLabel } from '../../../lib/deleteLabel'
import { userDisplayName } from '../userDisplayName'

interface ColumnOpts {
  t: TFunction
  /** Aktif dil EN mi (rol/grup çip etiketinde nameEn/nameTr seçimi). */
  en: boolean
  /** Durum değişimi süren satır — o satırın switch'i kilitlenir. */
  busyId: number | null
  onRoles: (row: UserWithRoles) => void
  onToggle: (row: UserWithRoles, active: boolean) => void
  /**
   * Salt okunur kip (126, Kısıtlı Yetkili asistan): switch durumu okunur kalsın diye görünür ama pasif;
   * Roller düğmesi çizilmez — kullanıcının rolleri "Roller" kolonunda zaten çip olarak okunuyor.
   */
  readOnly?: boolean
}

/**
 * Kullanıcı tablosu sütunları: salt-okuma alanlar + atanmış Roller + dahil olduğu Gruplar (122) + Durum + işlemler
 * (Roller dialogu ve Aktif/Pasif switch'i). Kullanıcı kaydı bu ekranda oluşturulmaz/silinmez —
 * USERS kimlik sağlayıcısından gelir; switch yalnız kaydın kullanılabilirliğini çevirir.
 */
export function userColumns({
  t,
  en,
  busyId,
  onRoles,
  onToggle,
  readOnly = false,
}: ColumnOpts): ReactElement[] {
  const label = (r: RoleRef | GroupRef) => (en && r.nameEn ? r.nameEn : r.nameTr)
  const chips = (items: (RoleRef | GroupRef)[] | undefined) =>
    (items ?? []).length ? (
      <div className="admin-role-chips">
        {(items ?? []).map((r) => (
          <Badge key={r.id} tone="neutral">
            {label(r)}
          </Badge>
        ))}
      </div>
    ) : (
      ''
    )
  return [
    <Column key="name" field="name" header={t('admin.users.columns.name')} sortable />,
    <Column key="surname" field="surname" header={t('admin.users.columns.surname')} sortable />,
    <Column
      key="userName"
      field="userName"
      header={t('admin.users.columns.userName')}
      body={(row: UserWithRoles) => row.userName ?? ''}
    />,
    <Column
      key="email"
      field="email"
      header={t('admin.users.columns.email')}
      body={(row: UserWithRoles) => row.email ?? ''}
    />,
    <Column
      key="roles"
      header={t('admin.users.columns.roles')}
      body={(row: UserWithRoles) => chips(row.roles)}
    />,
    <Column
      key="groups"
      header={t('admin.users.columns.groups')}
      body={(row: UserWithRoles) => chips(row.groups)}
    />,
    <Column
      key="status"
      header={t('admin.users.columns.status')}
      style={{ width: '8rem' }}
      body={(row: UserWithRoles) => {
        const active = row.recordStatus === 1
        return (
          <Badge tone={active ? 'green' : 'neutral'}>
            {active ? t('admin.users.status.active') : t('admin.users.status.passive')}
          </Badge>
        )
      }}
    />,
    <Column
      key="actions"
      header={t('admin.users.columns.actions')}
      style={{ width: '18rem' }}
      body={(row: UserWithRoles) => {
        const active = row.recordStatus === 1
        return (
          <div className="admin-row-actions">
            <InputSwitch
              className="admin-switch"
              checked={active}
              disabled={readOnly || busyId === row.id}
              onChange={(e) => onToggle(row, Boolean(e.value))}
              aria-label={`${row.name} ${row.surname}`}
              tooltip={t('admin.users.actions.toggle')}
              tooltipOptions={{ position: 'bottom' }}
            />
            {!readOnly && (
              <Button
                icon="pi pi-users"
                rounded
                text
                label={t('admin.users.actions.roles')}
                // Erişilebilir ad satırın kullanıcısını TAŞIR: sabit "Roller" deseydi ekran okuyucu
                // ve sesli kontrol kullanıcısı satırları ayırt edemezdi. Görünen etiket kısa kalır —
                // görsel kullanıcı satırın hizasından kimin olduğunu zaten görüyor.
                aria-label={rowActionLabel(t, 'admin.users.actions.roles', userDisplayName(row))}
                onClick={() => onRoles(row)}
              />
            )}
          </div>
        )
      }}
    />,
  ]
}
